import "./list-employee-favorite.scss";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import SearchInput from "@shared/ui/search-input/search-input";
import HeaderContent from "@shared/ui/header-content/header-content";
import {
  useQyDeleteEmployeeFavorite,
  useQyGetEmployeeFavorite,
} from "@core/query/employee-favorite.query";
import { GetEmployeeFavoriteModel } from "@core/model/query-model-employee-favorite";
import { TableSort } from "@core/model/table-sort";
import { FilterEmployeeFavorite } from "../filter-employee-favorite/filter-employee-favorite";
import { useFilterEmployeeFavoriteContext } from "../filter-employee-favorite/filter-employee-favorite.context";
import { useNotificationContext } from "@shared/ui/notification/notification.context";

export function ListEmployeeFavorite() {
  // THIS IS THE LOCAL DECLARATION
  const { showSuccess, showWarning } = useNotificationContext();
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterEmployeeFavoriteContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: employeeFavoriteResponse,
    isLoading,
    isError,
    error,
  } = useQyGetEmployeeFavorite(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  // API DELETE EmployeeFavorite
  const handleDeleteApiSuccess = () => {
    showSuccess("Employee deleted successfully");
  };
  const { mutate: deleteEmployeeFavorite } = useQyDeleteEmployeeFavorite(
    handleDeleteApiSuccess
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };
  const handleDelete = (code: string) => {
    deleteEmployeeFavorite({ code });
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterEmployeeFavorite />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{employeeFavoriteResponse?.count}</b>
      </h5>

      <DataTable
        value={employeeFavoriteResponse?.data}
        selectionMode="single"
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column field="favorite_name" header="Group Name" sortable></Column>
        <Column field="branch_name" header="Branch" sortable></Column>
        <Column field="department_name" header="Department" sortable></Column>
        <Column field="job_title_name" header="Job" sortable></Column>
        <Column
          body={(cell) =>
            `${cell.person_last_name}, ${cell.person_first_name} ${cell.person_middle_name}`
          }
          header="FirstName"
          sortable
        ></Column>
        <Column
          body={(cell) => (
            <Button
              label="Remove"
              outlined
              severity="secondary"
              size="small"
              onClick={() => handleDelete(cell.code)}
            />
          )}
          header="Actions"
          sortable
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={employeeFavoriteResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="EmployeeFavorite" className="employee-favorite">
      <HeaderContent title="Employee Group">
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListEmployeeFavorite;
